import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import websiteIllustration from '../assets/images/3.png';
import ProposalForm from "./ProposalForm";

const BRAND_GRADIENT = "linear-gradient(135deg, #0047ff, #00c4ff)";

/* ===================== WRAPPER ===================== */

const Wrapper = styled.div`
  font-family: 'Poppins', sans-serif;
  min-height: 100vh;
  color: #e5e7eb;
  padding: 80px 16px;
  overflow-x: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

/* ===================== HERO ===================== */

const Section = styled(motion.section)`
  text-align: center;
  padding: 40px 20px;
`;

const Title = styled.h1`
  font-size: 2.4rem;
  font-weight: 700;
  margin-bottom: 20px;
  word-break: break-word;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Paragraph = styled.p`
  max-width: 820px;
  margin: 0 auto;
  font-size: 1.1rem;
  line-height: 1.6;
  color: #cbd5e1;
`;

const CtaButton = styled(motion.button)`
  margin-top: 30px;
  padding: 14px 34px;
  border-radius: 50px;
  border: none;
  background: ${BRAND_GRADIENT};
  color: #fff;
  font-weight: 600;
  font-size: 1.05rem;
  cursor: pointer;
`;

/* ===================== WHY SECTION ===================== */

const WhySection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
  max-width: 900px;
  width: 100%;
  margin: 70px auto;
  padding: 0 16px;
`;

const WhyText = styled.div`
  max-width: 720px;
`;

const WhyTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 16px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const WhyPara = styled.p`
  font-size: 1.05rem;
  line-height: 1.7;
  color: #cbd5e1;
`;

const WhyImage = styled.img`
  width: 100%;
  max-width: 340px;
  height: auto;
  border-radius: 20px;
  display: block;
  margin: 0 auto;
`;

/* ===================== GRID ===================== */

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin: 60px auto 0;
  padding: 0 12px;
  justify-items: center;

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
    padding: 0 8px;
  }
`;

const Card = styled(motion.div)`
  width: 100%;
  max-width: 320px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(14px);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.12);

  @media (max-width: 360px) {
    max-width: 100%;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 10px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CardText = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #cbd5e1;
`;

/* ===================== DATA ===================== */

const technologies = [
  {
    title: 'React.js Development',
    description:
      'We build high-performance React.js web applications with scalable architecture and reusable components.',
  },
  {
    title: 'Angular Development',
    description:
      'We deliver dynamic and structured Angular applications ideal for enterprise-grade web platforms.',
  },
  {
    title: 'Custom Web Apps',
    description:
      'Tailored solutions using React or Angular to match unique business workflows and user experiences.',
  },
  {
    title: 'Progressive Web Apps (PWA)',
    description:
      'Build fast, installable, and offline-first web apps with React and Angular using modern standards.',
  },
  {
    title: 'UI/UX Design for Web',
    description:
      'Pixel-perfect design approach with user-first interactions and seamless navigation.',
  },
  {
    title: 'API Integration & Backend',
    description:
      'Integrate RESTful and GraphQL APIs and manage scalable backend solutions with Node.js or Firebase.',
  },
];

/* ===================== COMPONENT ===================== */

export default function WebApp() {

  const [showProposal, setShowProposal] = useState(false);

  return (
    <>
      <Wrapper>
        <Container>

          {/* HERO */}
          <Section
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
          >
            <Title>Web App Development</Title>

            <Paragraph>
              We specialize in building modern, fast, and scalable web applications using the latest technologies including React.js
              and Angular. From small startups to large enterprises, our web solutions are tailored to meet your goals and exceed user
              expectations.
            </Paragraph>

            <CtaButton
              whileHover={{
                scale: 1.08,
                boxShadow: "0 0 20px rgba(0,196,255,0.45)",
              }}
              whileTap={{ scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 260, damping: 18 }}
              onClick={() => setShowProposal(true)}
            >
              Request a Proposal
            </CtaButton>
          </Section>

          {/* WHY SECTION */}
          <WhySection>
            <WhyText>
              <WhyTitle>Why Develop a Website?</WhyTitle>
              <WhyPara>
                A professional website builds credibility, expands your global reach, and works 24/7 to represent your brand.
                In today’s digital-first world, customers search online before making decisions. A powerful web application
                enhances user experience, increases engagement, and drives measurable business growth.
                With scalable architecture and responsive design, your website becomes your most valuable digital asset.
              </WhyPara>
            </WhyText>

            <WhyImage
              src={websiteIllustration}
              alt="Website Development Illustration"
            />
          </WhySection>

          {/* GRID */}
          <Grid>
            {technologies.map(({ title, description }) => (
              <Card
                key={title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                whileHover={{
                  y: -10,
                  scale: 1.04,
                  boxShadow: "0 0 30px rgba(0,196,255,0.3)",
                }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                <CardTitle>{title}</CardTitle>
                <CardText>{description}</CardText>
              </Card>
            ))}
          </Grid>

        </Container>
      </Wrapper>

      <ProposalForm open={showProposal} setOpen={setShowProposal} />
    </>
  );
}